import type { Card, HkCard, HkPile, SeatCards } from "../gen/bazivan/v1/table_pb";

/**
 * قفلِ پخش — همتای Backend/internal/fair و Bazivan.Core.DealLock در اپ.
 *
 * سرور سرِ پخش SHA-256 ِ همه‌ی دسته‌ها و یک راز را می‌فرستد (`deal_lock` در
 * وضعیتِ میز) و پایانِ دست رو می‌کند. این‌جا دوباره هش می‌گیریم و می‌سنجیم که
 * هم با قفل جور است و هم هر کارتی که به دستم رسید در دسته‌ی قفل‌شده‌ی خودم بود.
 * یعنی پخش بعد از حکم‌خواندن یا وسطِ بازی عوض نشده — جوابِ «ورق را جور
 * می‌چینند» که شکایتِ اولِ بازی‌های کارتیِ رقیب در بازار است
 * (docs/competitor-reviews.md).
 *
 * قالب با Backend/testdata/deal_lock_vectors.json سنجیده می‌شود
 * (test/deal-lock.test.ts)؛ هر تغییری باید در هر سه سو باشد.
 */
export const VERSION = "bazivan-deal-v1";

export type Result = "verified" | "lock_mismatch" | "my_hand_mismatch";

const RANKS = "A23456789TJQK";
const SUITS = "HDSC";
const SPECIAL: Record<number, string> = { 1: "XM", 2: "XB", 3: "XS", 4: "XC" };

/** کدِ کوتاه، مثلِ سرور: "AH"، "TD"؛ ویژه‌های هفت‌خبیث "X" + حرف. */
export function cardCode(c: Card | undefined, special = 0): string {
  if (special) return SPECIAL[special] ?? "";
  if (!c) return "";
  return (RANKS[c.rank - 1] ?? "?") + (SUITS[c.suit - 1] ?? "?");
}

export const hkCode = (c: HkCard): string => cardCode(c.card, c.special);

/** رشته‌ای که هش می‌شود: هر دسته یک سطر، کدها مرتب (بایت‌به‌بایت، مثلِ sort.Strings ِ Go). */
export function canonical(secret: string, piles: string[][]): string {
  const lines = piles.map((p) => [...p].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).join(","));
  return [VERSION, secret, ...lines].join("\n");
}

export async function lockOf(secret: string, piles: string[][]): Promise<string> {
  const bytes = new TextEncoder().encode(canonical(secret, piles));
  const sum = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(sum, (x) => x.toString(16).padStart(2, "0")).join("");
}

/**
 * سنجش: هش با قفل، و هر کارتی که در این دست دیدم در دسته‌ی خودم — یا دسته‌ای که
 * مجاز بود از آن بگیرم (`also`، مثلِ زمینِ شلم برای حاکم) — هست. «زیرمجموعه» و نه
 * «برابر»: کسی که وسطِ دست وصل شد کارت‌های بازی‌شده‌اش را ندیده.
 */
export async function verify(lock: string, secret: string, piles: string[][], mySeat: number,
                             seen: Set<string>, also: number[] = []): Promise<Result | null> {
  if (!lock || !secret || piles.length === 0 || seen.size === 0) return null;
  if ((await lockOf(secret, piles)) !== lock.toLowerCase()) return "lock_mismatch";
  const allowed = new Set(piles[mySeat] ?? []);
  for (const k of also) for (const c of piles[k] ?? []) allowed.add(c);
  for (const c of seen) if (!allowed.has(c)) return "my_hand_mismatch";
  return "verified";
}

/** هفت‌خبیث: فقط پخشِ آغاز قفل است؛ دستِ آغازم باید دقیقاً دسته‌ی خودم باشد (با شمارشِ ویژه‌های تکراری). */
export async function verifyOpening(lock: string, secret: string, piles: string[][], mySeat: number,
                                    opening: string[] | null): Promise<Result | null> {
  if (!lock || !secret || piles.length === 0 || !opening || opening.length === 0) return null;
  if ((await lockOf(secret, piles)) !== lock.toLowerCase()) return "lock_mismatch";
  const sorted = (xs: string[]) => [...xs].sort().join(",");
  return sorted(opening) === sorted(piles[mySeat] ?? []) ? "verified" : "my_hand_mismatch";
}

/**
 * پیگیریِ قفل در کنترلر: قفلِ دستِ جاری و هر کارتی که در این دست به دستم رسید
 * (اجتماع — دستم با بازی کوچک و با پخشِ بعدی بزرگ می‌شود). قفلِ تازه یعنی دستِ
 * تازه. از صفِ صحنه صدا زده می‌شود تا وضعیتِ دستِ بعد قاطیِ سنجشِ این دست نشود.
 */
export class DealWatch {
  lock = "";
  readonly seen = new Set<string>();
  /** دستِ آغازِ دور، فقط اگر لحظه‌ی پخش را دیدیم (هفت‌خبیث). */
  opening: string[] | null = null;

  observe(lock: string, hand: string[], fresh = false): void {
    if (lock !== this.lock) {
      this.lock = lock;
      this.seen.clear();
      this.opening = fresh ? [...hand] : null;
    }
    for (const c of hand) this.seen.add(c);
  }
}

/** آن‌چه برگه‌ی «کارت‌های همه» نشان می‌دهد. */
export type DealProof = {
  result: Result;
  /** کارتِ هر صندلی به ایندکسِ **محلی**. */
  seats: Card[][];
  extra?: { name: string; cards: Card[] };
  note: string;
};

/** دسته‌های رو‌شده‌ی SeatCards → کد و کارت. */
export function seatPiles(revealed: SeatCards[]): { codes: string[][]; cards: Card[][] } | null {
  const codes: string[][] = [], cards: Card[][] = [];
  for (const sc of revealed) {
    codes[sc.seat] = sc.cards.map((c) => cardCode(c));
    cards[sc.seat] = sc.cards;
  }
  for (let i = 0; i < revealed.length; i++) if (!codes[i]) return null;
  return { codes, cards };
}

/**
 * از رو‌کردنِ پایانِ دست یک DealProof می‌سازد (حکم، شلم، چهاربرگ). null یعنی
 * سنجشی در کار نیست — سرورِ قدیمی یا قفلی ندیدیم — و نشانی هم نمی‌آید: نبودنِ
 * نشان بهتر از «نمی‌خواند»ِ دروغ است.
 */
export async function buildProof(w: DealWatch, secret: string, revealed: SeatCards[], seats: number, me: number,
                                 toLocal: (server: number) => number, note: string,
                                 extra?: { pile: number; name: string; mayTake?: boolean }): Promise<DealProof | null> {
  const p = seatPiles(revealed);
  if (!p || p.codes.length < seats) return null;
  const result = await verify(w.lock, secret, p.codes, me, w.seen, extra?.mayTake ? [extra.pile] : []);
  if (!result) return null;
  const local: Card[][] = [];
  for (let k = 0; k < seats; k++) local[toLocal(k)] = p.cards[k]!;
  return { result, seats: local, note, extra: extra ? { name: extra.name, cards: p.cards[extra.pile] ?? [] } : undefined };
}

/** هفت‌خبیث: دسته‌ها صندلی‌ها، زمینِ آغاز، دسته‌ی کشیدنی. */
export async function buildOpeningProof(w: DealWatch, secret: string, revealed: HkPile[], seats: number, me: number,
                                        toLocal: (server: number) => number, note: string): Promise<DealProof | null> {
  if (revealed.length !== seats + 2) return null;
  const codes: string[][] = [], cards: HkCard[][] = [];
  for (const p of revealed) { codes[p.seat] = p.cards.map(hkCode); cards[p.seat] = p.cards; }
  for (let i = 0; i < revealed.length; i++) if (!codes[i]) return null;
  const result = await verifyOpening(w.lock, secret, codes, me, w.opening);
  if (!result) return null;
  // ویژه‌ها در برگه با نامشان می‌آیند؛ آن‌ها را جدا نگه نمی‌داریم — آنلاین امروز بی‌ویژه است.
  const plain = (hs: HkCard[]) => hs.filter((h) => !h.special && h.card).map((h) => h.card!);
  const local: Card[][] = [];
  for (let k = 0; k < seats; k++) local[toLocal(k)] = plain(cards[k]!);
  return { result, seats: local, note, extra: { name: "زمین آغاز", cards: plain(cards[seats]!) } };
}
